// src/pages/UsersAdmin.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../auth/api";
import { useAuth } from "../auth/AuthContext";

export default function UsersAdmin() {
  const { user } = useAuth();

  // lista de usuarios
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // departamentos
  const [departments, setDepartments] = useState([]);
  const [depLoading, setDepLoading] = useState(false);
  const [depName, setDepName] = useState("");
  const [depCreating, setDepCreating] = useState(false);

  // ✅ edición departamentos (inline)
  const [depEditId, setDepEditId] = useState(null);
  const [depEditName, setDepEditName] = useState("");
  const [depSaving, setDepSaving] = useState(false);
  const [depDeleting, setDepDeleting] = useState(false);

  // alta usuario (incluye org fields)
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "operativo",
    job_title: "",
    department_id: "",
    manager_id: "",
    phone: "",
    org_level: 0,
    active: 1,
    timezone: "America/Guayaquil",
  });
  const [creating, setCreating] = useState(false);

  // edición (modal simple)
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editRow, setEditRow] = useState(null);

  // drawer de asignación (cuentas)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null); // usuario seleccionado
  const [accLoading, setAccLoading] = useState(false);
  const [accRows, setAccRows] = useState([]); // [{id,label,provider,status,assigned,user_role}]
  const [accQuery, setAccQuery] = useState("");
  const [savingAssign, setSavingAssign] = useState(false);
  const drawerRef = useRef(null);

  // filtros de tabla (rápidos)
  const [qUsers, setQUsers] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [depFilter, setDepFilter] = useState("");

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // usa /users/ops si existe, sino cae a /users
      let data;
      try {
        const resp = await api.get("/users/ops");
        data = resp.data;
      } catch {
        const resp = await api.get("/users");
        data = resp.data;
      }
      setRows(data?.rows || []);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    setDepLoading(true);
    try {
      const { data } = await api.get("/departments");
      setDepartments(data?.rows || []);
    } catch (e) {
      setDepartments([]);
    } finally {
      setDepLoading(false);
    }
  };

  const createDepartment = async (e) => {
    e.preventDefault();
    const name = depName.trim();
    if (!name) return;
    setDepCreating(true);
    try {
      await api.post("/departments", { name });
      setDepName("");
      await fetchDepartments();
    } catch (e2) {
      alert(e2?.response?.data?.error || "No se pudo crear departamento");
    } finally {
      setDepCreating(false);
    }
  };

  // ✅ editar / renombrar departamento
  const startEditDepartment = (d) => {
    setDepEditId(d.id);
    setDepEditName(d.name || "");
  };

  const cancelEditDepartment = () => {
    setDepEditId(null);
    setDepEditName("");
  };

  const saveDepartment = async (e) => {
    e?.preventDefault?.();
    if (!depEditId) return;

    const name = depEditName.trim();
    if (!name) {
      alert("Nombre requerido");
      return;
    }

    setDepSaving(true);
    try {
      await api.patch(`/departments/${depEditId}`, { name });
      await fetchDepartments();
      cancelEditDepartment();
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo actualizar el departamento");
    } finally {
      setDepSaving(false);
    }
  };

  // ✅ eliminar departamento
  const deleteDepartment = async (id) => {
    if (!window.confirm("¿Eliminar departamento? (solo si no tiene usuarios)")) return;

    setDepDeleting(true);
    try {
      await api.delete(`/departments/${id}`);
      await fetchDepartments();
      if (String(depFilter) === String(id)) setDepFilter("");
      if (depEditId === id) cancelEditDepartment();
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo eliminar el departamento");
    } finally {
      setDepDeleting(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      alert("Completa nombre, email y contraseña");
      return;
    }

    setCreating(true);
    try {
      // 1) crear user base (route existente)
      const payloadBase = {
        name: form.name,
        email: form.email,
        password: form.password,
        // tu backend usa roles admin/agent/viewer, tu UI usa operativo/admin.
        // Ajuste: operativo => agent
        role:
          form.role === "admin" ? "admin" :
            form.role === "viewer" ? "viewer" :
              "agent",
      };

      const { data } = await api.post("/users", payloadBase);
      const newId = data?.id;

      // 2) opcional: actualizar profile organizacional
      if (newId) {
        const profile = {
          name: form.name,
          job_title: form.job_title || null,
          department_id: form.department_id ? Number(form.department_id) : null,
          manager_id: form.manager_id ? Number(form.manager_id) : null,
          phone: form.phone || null,
          timezone: form.timezone || "America/Guayaquil",
          org_level: Number(form.org_level || 0),
          active: Number(form.active || 1),
        };
        try {
          await api.patch(`/users/${newId}/profile`, profile);
        } catch {
          // si profile route no está, no bloqueamos creación
        }
      }

      setForm({
        name: "",
        email: "",
        password: "",
        role: "operativo",
        job_title: "",
        department_id: "",
        manager_id: "",
        phone: "",
        org_level: 0,
        active: 1,
        timezone: "America/Guayaquil",
      });

      await fetchUsers();
    } catch (e2) {
      alert(e2?.response?.data?.error || "Error al crear usuario");
    } finally {
      setCreating(false);
    }
  };

  // soft delete (activar/desactivar)
  const deactivate = async (id) => {
    if (!window.confirm("¿Desactivar usuario?")) return;
    try {
      await api.patch(`/users/${id}/active`, { active: 0 });
      if (selected?.id === id) closeDrawer();
      await fetchUsers();
    } catch (e) {
      alert(e?.response?.data?.error || "Error al desactivar");
    }
  };

  const activate = async (id) => {
    try {
      await api.patch(`/users/${id}/active`, { active: 1 });
      await fetchUsers();
    } catch (e) {
      alert(e?.response?.data?.error || "Error al activar");
    }
  };

  // --- Drawer: asignación de cuentas
  const openAssign = async (u) => {
    setSelected(u);
    setDrawerOpen(true);
    setAccQuery("");
    setAccLoading(true);
    try {
      const { data } = await api.get(`/users/${u.id}/accounts`);
      const list = (data?.accounts || []).map((a) => ({
        id: a.id,
        label: a.label,
        provider: a.provider,
        status: a.status,
        assigned: !!a.assigned,
        user_role: a.user_role || "agent",
      }));
      setAccRows(list);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cargar las cuentas del usuario");
      setAccRows([]);
    } finally {
      setAccLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setAccRows([]);
    setAccQuery("");
    setSavingAssign(false);
  };

  // cierre con ESC
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        if (drawerOpen) closeDrawer();
        if (editOpen) closeEdit();
        if (depEditId) cancelEditDepartment();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, editOpen, depEditId]);

  const toggleAcc = (id, checked) => {
    setAccRows((arr) => arr.map((a) => (a.id === id ? { ...a, assigned: checked } : a)));
  };
  const changeAccRole = (id, role) => {
    setAccRows((arr) => arr.map((a) => (a.id === id ? { ...a, user_role: role } : a)));
  };
  const selectAll = (checked) => {
    setAccRows((arr) => arr.map((a) => ({ ...a, assigned: checked })));
  };
  const saveAssignments = async () => {
    if (!selected) return;
    setSavingAssign(true);
    try {
      const assignments = accRows
        .filter((a) => a.assigned)
        .map((a) => ({ wa_account_id: a.id, role: a.user_role || "agent" }));
      await api.post(`/users/${selected.id}/accounts`, { assignments });
      setSavingAssign(false);
      closeDrawer();
    } catch (e) {
      setSavingAssign(false);
      alert(e?.response?.data?.error || "No se pudo guardar asignaciones");
    }
  };

  // --- Edit profile modal
  const openEdit = (u) => {
    setEditRow({
      id: u.id,
      name: u.name || "",
      email: u.email || "",
      role: u.role || "agent",
      job_title: u.job_title || "",
      department_id: u.department_id ? String(u.department_id) : "",
      manager_id: u.manager_id ? String(u.manager_id) : "",
      phone: u.phone || "",
      org_level: typeof u.org_level === "number" ? u.org_level : Number(u.org_level || 0),
      active: typeof u.active === "number" ? u.active : Number(u.active ?? 1),
      timezone: u.timezone || "America/Guayaquil",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!editRow?.id) return;
    setEditing(true);
    try {
      const payload = {
        name: editRow.name,
        job_title: editRow.job_title || null,
        department_id: editRow.department_id ? Number(editRow.department_id) : null,
        manager_id: editRow.manager_id ? Number(editRow.manager_id) : null,
        phone: editRow.phone || null,
        timezone: editRow.timezone || "America/Guayaquil",
        org_level: Number(editRow.org_level || 0),
        active: Number(editRow.active || 1),
      };
      await api.patch(`/users/${editRow.id}/profile`, payload);

      await fetchUsers();
      closeEdit();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo guardar el perfil");
      setEditing(false);
    }
  };

  const prettyChip = (text, tone = "muted") => {
    const bg =
      tone === "ok"
        ? "rgba(34,197,94,.15)"
        : tone === "warn"
          ? "rgba(244,63,94,.15)"
          : "rgba(148,163,184,.20)";
    const fg = tone === "ok" ? "#16a34a" : tone === "warn" ? "#ef4444" : "#94a3b8";
    return (
      <span
        style={{
          background: bg,
          color: fg,
          fontSize: 12,
          borderRadius: 999,
          padding: "2px 8px",
          fontWeight: 600,
          display: "inline-block",
        }}
      >
        {text}
      </span>
    );
  };

  const filteredAccs = accRows.filter((a) => {
    const q = accQuery.trim().toLowerCase();
    if (!q) return true;
    return a.label.toLowerCase().includes(q) || a.provider.toLowerCase().includes(q) || String(a.id).includes(q);
  });

  const usersForManagers = useMemo(() => {
    return (rows || [])
      .filter((u) => Number(u.active ?? 1) === 1)
      .sort(
        (a, b) =>
          Number(b.org_level || 0) - Number(a.org_level || 0) || String(a.name).localeCompare(String(b.name))
      );
  }, [rows]);

  const departmentName = (department_id) => {
    const id = Number(department_id || 0);
    const d = departments.find((x) => x.id === id);
    return d?.name || "";
  };

  const shownUsers = useMemo(() => {
    const q = qUsers.trim().toLowerCase();
    return (rows || []).filter((u) => {
      if (onlyActive && Number(u.active ?? 1) !== 1) return false;
      if (depFilter && String(u.department_id || "") !== String(depFilter)) return false;

      if (!q) return true;
      return (
        String(u.id).includes(q) ||
        String(u.name || "").toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q) ||
        String(u.job_title || "").toLowerCase().includes(q) ||
        String(departmentName(u.department_id) || "").toLowerCase().includes(q) ||
        String(u.phone || "").toLowerCase().includes(q)
      );
    });
  }, [rows, qUsers, onlyActive, depFilter, departments]);

  if (user?.role !== "admin") return <div className="section">Solo administradores.</div>;

  return (
    <>
      <div className="page" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        {/* Crear usuario */}
        <div className="card section" style={{ display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <h2 style={{ margin: 0 }}>Usuarios</h2>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, opacity: 0.85 }}>
                {loading ? "Cargando usuarios…" : `Total: ${rows.length}`}
              </span>
            </div>
          </div>

          <form
            onSubmit={createUser}
            style={{
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))",
              alignItems: "end",
            }}
          >
            <label>
              Nombre
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              Email
              <input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label>
              Contraseña
              <input
                className="input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </label>

            <label>
              Rol sistema
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="admin">Solo Ver</option>
                <option value="operativo">Operativo</option>
                <option value="admin">Administrador</option>
              </select>
            </label>

            <label>
              Cargo
              <input
                className="input"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                placeholder="Ej: Asesor Comercial"
              />
            </label>

            <label>
              Departamento
              <select
                className="input"
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                disabled={depLoading}
              >
                <option value="">—</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Jefe directo
              <select className="input" value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                <option value="">—</option>
                {usersForManagers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} (lvl {Number(u.org_level || 0)})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Teléfono
              <input
                className="input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Ej: 0999999999"
              />
            </label>

            <label>
              Nivel org (org_level)
              <input
                className="input"
                type="number"
                value={form.org_level}
                onChange={(e) => setForm({ ...form, org_level: Number(e.target.value || 0) })}
                min={0}
                max={999}
              />
            </label>

            <label>
              Estado
              <select className="input" value={String(form.active)} onChange={(e) => setForm({ ...form, active: Number(e.target.value) })}>
                <option value="1">Activo</option>
                <option value="0">Inactivo</option>
              </select>
            </label>

            <label>
              Timezone
              <input
                className="input"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                placeholder="America/Guayaquil"
              />
            </label>

            <button className="btn btn-primary" disabled={creating} style={{ height: 40 }}>
              {creating ? "Creando..." : "Crear usuario"}
            </button>
          </form>
        </div>

        {/* Departamentos: mini gestión + actualizar/eliminar */}
        <div className="card section" style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ margin: 0 }}>Departamentos</h3>
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              {depLoading ? "Cargando…" : `${departments.length} departamentos`}
            </span>
          </div>

          <form onSubmit={createDepartment} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              className="input"
              value={depName}
              onChange={(e) => setDepName(e.target.value)}
              placeholder="Nuevo departamento (ej: Ventas)"
              style={{ minWidth: 260 }}
            />
            <button className="btn btn-muted" disabled={depCreating || !depName.trim()}>
              {depCreating ? "Creando…" : "Agregar"}
            </button>
            <button className="btn btn-muted" type="button" onClick={fetchDepartments}>
              Refrescar
            </button>
          </form>

          {departments.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>ID</th>
                    <th>Nombre</th>
                    <th style={{ width: 260 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((d) => {
                    const editingThis = depEditId === d.id;

                    return (
                      <tr key={d.id}>
                        <td>{d.id}</td>
                        <td>
                          {editingThis ? (
                            <input
                              className="input"
                              value={depEditName}
                              onChange={(e) => setDepEditName(e.target.value)}
                              autoFocus
                            />
                          ) : (
                            d.name
                          )}
                        </td>
                        <td style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          {editingThis ? (
                            <>
                              <button className="btn btn-primary" type="button" onClick={saveDepartment} disabled={depSaving}>
                                {depSaving ? "Guardando…" : "Guardar"}
                              </button>
                              <button className="btn btn-muted" type="button" onClick={cancelEditDepartment} disabled={depSaving}>
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-muted" type="button" onClick={() => startEditDepartment(d)}>
                                Renombrar
                              </button>
                              <button
                                className="btn btn-danger"
                                type="button"
                                onClick={() => deleteDepartment(d.id)}
                                disabled={depDeleting}
                                title="Solo si no tiene usuarios asignados"
                              >
                                {depDeleting ? "Eliminando…" : "Eliminar"}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                Nota: eliminar solo funciona si el departamento no tiene usuarios (backend devuelve 409 si está en uso).
              </div>
            </div>
          )}
        </div>

        {/* Listado */}
        <div className="card section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ margin: 0 }}>Listado</h3>
            {loading && <span style={{ fontSize: 12, opacity: 0.8 }}>Cargando…</span>}
          </div>

          {/* filtros */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
            <input
              className="input"
              placeholder="Buscar por nombre, email, cargo, depto, teléfono, id…"
              value={qUsers}
              onChange={(e) => setQUsers(e.target.value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <select className="input" value={depFilter} onChange={(e) => setDepFilter(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">Todos los departamentos</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, opacity: 0.9 }}>
              <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
              Solo activos
            </label>
            <button className="btn btn-muted" onClick={fetchUsers} type="button">
              Refrescar
            </button>
          </div>

          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Cargo</th>
                  <th>Departamento</th>
                  <th>Jefe</th>
                  <th>Nivel</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shownUsers.map((r) => {
                  const mgr = rows.find((x) => String(x.id) === String(r.manager_id));
                  const dept = departmentName(r.department_id);
                  const active = Number(r.active ?? 1) === 1;

                  return (
                    <tr key={r.id} style={{ opacity: active ? 1 : 0.65 }}>
                      <td>{r.id}</td>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.role}</td>
                      <td>{r.job_title || "-"}</td>
                      <td>{dept || "-"}</td>
                      <td>{mgr?.name || "-"}</td>
                      <td>{Number(r.org_level || 0)}</td>
                      <td>{active ? prettyChip("Activo", "ok") : prettyChip("Inactivo", "warn")}</td>
                      <td>{r.created_at?.replace("T", " ").slice(0, 19)}</td>
                      <td style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button className="btn" onClick={() => openAssign(r)} type="button">
                          Asignar canales
                        </button>
                        <button className="btn btn-muted" onClick={() => openEdit(r)} type="button">
                          Editar perfil
                        </button>
                        {user?.id !== r.id &&
                          (active ? (
                            <button className="btn btn-danger" onClick={() => deactivate(r.id)} type="button">
                              Desactivar
                            </button>
                          ) : (
                            <button className="btn btn-muted" onClick={() => activate(r.id)} type="button">
                              Activar
                            </button>
                          ))}
                      </td>
                    </tr>
                  );
                })}
                {shownUsers.length === 0 && (
                  <tr>
                    <td colSpan={11}>Sin usuarios.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer lateral: Asignación de canales */}
      {drawerOpen && (
        <>
          <div
            onClick={closeDrawer}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.35)",
              backdropFilter: "blur(2px)",
              zIndex: 40,
            }}
          />
          <div
            ref={drawerRef}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              bottom: 0,
              width: "min(560px, 100vw)",
              background: "var(--bg, #0b1220)",
              color: "#eef2ff",
              boxShadow: "-6px 0 24px rgba(0,0,0,.35)",
              zIndex: 50,
              display: "grid",
              gridTemplateRows: "auto auto 1fr auto",
              animation: "slideIn .18s ease-out",
            }}
          >
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid rgba(255,255,255,.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>Asignación de canales</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>
                  {selected?.name} <span style={{ opacity: 0.75, fontWeight: 500 }}>· {selected?.email}</span>
                </div>
              </div>
              <button className="btn btn-muted" onClick={closeDrawer} type="button">
                Cerrar
              </button>
            </div>

            <div
              style={{
                padding: "12px 18px",
                borderBottom: "1px solid rgba(255,255,255,.06)",
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                className="input"
                placeholder="Buscar cuenta, proveedor o ID…"
                value={accQuery}
                onChange={(e) => setAccQuery(e.target.value)}
                style={{ minWidth: 220 }}
              />
              <div style={{ flex: 1 }} />
              <button className="btn btn-muted" onClick={() => selectAll(true)} type="button">
                Seleccionar todo
              </button>
              <button className="btn btn-muted" onClick={() => selectAll(false)} type="button">
                Quitar todo
              </button>
            </div>

            <div style={{ overflowY: "auto", padding: 12 }}>
              {accLoading ? (
                <div className="card section">Cargando cuentas…</div>
              ) : filteredAccs.length === 0 ? (
                <div className="card section">Sin resultados.</div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {filteredAccs.map((a) => (
                    <div
                      key={a.id}
                      className="card"
                      style={{
                        padding: 12,
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        gap: 12,
                        alignItems: "center",
                      }}
                    >
                      <input type="checkbox" checked={!!a.assigned} onChange={(e) => toggleAcc(a.id, e.target.checked)} />

                      <div style={{ display: "grid", gap: 2 }}>
                        <div style={{ fontWeight: 700 }}>{a.label}</div>
                        <div
                          style={{
                            fontSize: 12,
                            opacity: 0.85,
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>ID #{a.id}</span>
                          <span>·</span>
                          <span>{a.provider}</span>
                          <span>·</span>
                          {prettyChip(a.status === "active" ? "Activa" : "Inactiva", a.status === "active" ? "ok" : "warn")}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>Rol:</span>
                        <select
                          className="input"
                          value={a.user_role || "agent"}
                          onChange={(e) => changeAccRole(a.id, e.target.value)}
                          disabled={!a.assigned}
                          style={{ minWidth: 140 }}
                        >
                          <option value="agent">Agente</option>
                          <option value="supervisor">Supervisor</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "12px 18px",
                borderTop: "1px solid rgba(255,255,255,.08)",
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
              }}
            >
              <button className="btn btn-muted" onClick={closeDrawer} type="button">
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={saveAssignments} disabled={savingAssign || accLoading} type="button">
                {savingAssign ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>

          <style>{`@keyframes slideIn { from { transform: translateX(12px); opacity:.0 } to { transform: translateX(0); opacity:1 } }`}</style>
        </>
      )}

      {/* Modal Editar Perfil */}
      {editOpen && editRow && (
        <>
          <div
            onClick={closeEdit}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.35)",
              backdropFilter: "blur(2px)",
              zIndex: 60,
            }}
          />
          <div
            style={{
              position: "fixed",
              zIndex: 70,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(720px, calc(100vw - 24px))",
              background: "var(--panel, #0f172a)",
              border: "1px solid rgba(255,255,255,.08)",
              borderRadius: 14,
              color: "#eef2ff",
              boxShadow: "0 20px 60px rgba(0,0,0,.45)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 14,
                borderBottom: "1px solid rgba(255,255,255,.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>Editar perfil</div>
                <div style={{ fontWeight: 800 }}>
                  {editRow.name} <span style={{ opacity: 0.75, fontWeight: 500 }}>· {editRow.email}</span>
                </div>
              </div>
              <button className="btn btn-muted" onClick={closeEdit} type="button">
                Cerrar
              </button>
            </div>

            <div style={{ padding: 14, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <label>
                Nombre
                <input className="input" value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} />
              </label>

              <label>
                Cargo
                <input className="input" value={editRow.job_title} onChange={(e) => setEditRow({ ...editRow, job_title: e.target.value })} />
              </label>

              <label>
                Departamento
                <select className="input" value={editRow.department_id} onChange={(e) => setEditRow({ ...editRow, department_id: e.target.value })}>
                  <option value="">—</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Jefe directo
                <select className="input" value={editRow.manager_id} onChange={(e) => setEditRow({ ...editRow, manager_id: e.target.value })}>
                  <option value="">—</option>
                  {usersForManagers
                    .filter((u) => String(u.id) !== String(editRow.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} (lvl {Number(u.org_level || 0)})
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Teléfono
                <input className="input" value={editRow.phone} onChange={(e) => setEditRow({ ...editRow, phone: e.target.value })} />
              </label>

              <label>
                org_level
                <input
                  className="input"
                  type="number"
                  value={editRow.org_level}
                  onChange={(e) => setEditRow({ ...editRow, org_level: Number(e.target.value || 0) })}
                  min={0}
                  max={999}
                />
              </label>

              <label>
                Estado
                <select className="input" value={String(editRow.active)} onChange={(e) => setEditRow({ ...editRow, active: Number(e.target.value) })}>
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </label>

              <label>
                Timezone
                <input className="input" value={editRow.timezone} onChange={(e) => setEditRow({ ...editRow, timezone: e.target.value })} />
              </label>
            </div>

            <div style={{ padding: 14, borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn btn-muted" onClick={closeEdit} disabled={editing} type="button">
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editing} type="button">
                {editing ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
